import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useRef, useState} from 'react';

import {BLUE_DARKER} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import {Point} from 'util/geometry/GeometryConstants';

import {DWD_RELATIONSHIP_TOOLTIP_HEIGHT} from './diagram-layout/diagramLayoutConstants';
import {VerticalSide, WarehouseTooltipData} from './diagram-layout/diagramLayoutTypes';
import {getRelationshipDecoratorPixelPath, getRelationshipDecoratorTypes} from './relationshipPathDecorator';
import {stringifyPath} from './WarehouseDiagramRelationship';

const TOOLTIP_SVG_WIDTH = 40;

// pixel distance the tooltip should be offset from the mouse click location so as to not overlap with
// relationship paths
const TOOLTIP_RELATIONSHIP_PADDING = 7;

// Constants determined with visual tweaking
const TOOLTIP_SVG_START_POINT = {x: 2, y: 13};
const TOOLTIP_SVG_END_POINT = {x: 2, y: DWD_RELATIONSHIP_TOOLTIP_HEIGHT - 11};
const TOOLTIP_SVG_PATH = [
  TOOLTIP_SVG_START_POINT,
  {x: TOOLTIP_SVG_WIDTH - 15, y: TOOLTIP_SVG_START_POINT.y},
  {x: TOOLTIP_SVG_WIDTH - 15, y: TOOLTIP_SVG_END_POINT.y},
  TOOLTIP_SVG_END_POINT,
];

/**
 * Return the transform: translate3d(x, y, 0px) string that a tooltip should be placed at on the diagram.
 */
export function getRelationshipTooltipTranslation(
  triggerPoint: Point,
  diagramWrapperRef: React.RefObject<HTMLDivElement>,
  tooltipWidth: number | null
): string {
  const wrapperBoundingClientRect = diagramWrapperRef.current!.getBoundingClientRect();
  // Clicking on a relationship on the left half of the diagram opens the tooltip to the right of the click
  // position, while clicking on the right half of the diagram opens the tooltip to the left
  // of the click position.
  const showToRightOfMouse =
    // Mouse position relative to left side of diagram wrapper
    triggerPoint.x - wrapperBoundingClientRect.left <
    // Horizontal midpoint of diagram wrapper
    diagramWrapperRef.current!.offsetWidth / 2;

  const baseXTranslation = Math.round(
    // Mouse position minus wrapper's left offset
    triggerPoint.x -
      wrapperBoundingClientRect.left +
      // Plus some additional "padding" to move the tooltip to the left/right of the clicked relationship
      // (visually helpful if the clicked relationship segment is vertical)
      (showToRightOfMouse ? TOOLTIP_RELATIONSHIP_PADDING : -TOOLTIP_RELATIONSHIP_PADDING)
  );
  const baseYTranslation = Math.round(
    // Mouse position minus wrapper's top offset
    triggerPoint.y -
      wrapperBoundingClientRect.top -
      // Minus the tooltip's height to move the tooltip above the cursor
      DWD_RELATIONSHIP_TOOLTIP_HEIGHT -
      // Minus some additional "padding" to move the tooltip above the clicked
      // (visually helpful if the clicked relationship segment is horizontal)
      TOOLTIP_RELATIONSHIP_PADDING
  );

  // When the tooltip is to the right of the mouse, it can simply be translated over by the baseXTranslation
  // computed above. However, when the tooltip is to the left of the mouse, it needs to be translated
  // baseXTranslation minus the width of the tooltip. The width of the tooltip can either come from
  // a CSS calc (100%), or from the tooltip ref object.
  // The CSS calc leads to text blur in Chrome due to non-integer translation values. For more, see:
  // https://stackoverflow.com/questions/32034574/font-looks-blurry-after-translate-in-chrome
  // Therefore, it is preferred to use the tooltip's rounded width as the translation amount, preventing
  // bluriness.
  const xTranslation = showToRightOfMouse
    ? `${baseXTranslation}px`
    : tooltipWidth
      ? `${Math.ceil(baseXTranslation - tooltipWidth)}px`
      : `calc(${baseXTranslation}px - 100%)`;
  const yTranslation = `${baseYTranslation}px`;
  return `translate3d(${xTranslation}, ${yTranslation}, 0px)`;
}

export interface WarehouseRelationshipTooltipProps {
  tooltipData: WarehouseTooltipData;
  diagramWrapperRef: React.RefObject<HTMLDivElement>;
}

export default function WarehouseRelationshipTooltip({
  tooltipData: {relationshipData, triggerPoint},
  diagramWrapperRef,
}: WarehouseRelationshipTooltipProps): JSX.Element {
  const [tooltipTranslation, setTooltipTranslation] = useState<string>(
    getRelationshipTooltipTranslation(triggerPoint, diagramWrapperRef, null)
  );
  const tooltipWrapperRef = useRef<HTMLDivElement>(null);

  useOnMount(() => {
    if (tooltipWrapperRef.current) {
      setTooltipTranslation(
        getRelationshipTooltipTranslation(
          triggerPoint,
          diagramWrapperRef,
          tooltipWrapperRef.current.offsetWidth
        )
      );
    }
  });

  const {sourceDecoratorType, targetDecoratorType} = getRelationshipDecoratorTypes(relationshipData.type);
  const sourceDecoratorPath = getRelationshipDecoratorPixelPath(
    sourceDecoratorType,
    VerticalSide.RIGHT,
    TOOLTIP_SVG_START_POINT
  );
  const targetDecoratorPath = getRelationshipDecoratorPixelPath(
    targetDecoratorType,
    VerticalSide.RIGHT,
    TOOLTIP_SVG_END_POINT
  );

  const sourceTableColumnName = `${relationshipData.source.table.name}.${relationshipData.source.name}`;
  const targetTableColumnName = `${relationshipData.target.table.name}.${relationshipData.target.name}`;

  return (
    <div
      onMouseDown={(e) => {
        // Prevents clicking the tooltip from closing it.
        e.stopPropagation();
      }}
      className={classNames(css(styles.wrapper))}
      ref={tooltipWrapperRef}
      style={{
        transform: tooltipTranslation,
      }}
    >
      <div className={css(styles.relationshipDataDiv)}>
        <div className={css(styles.tableColumnNameDiv)}>{sourceTableColumnName}</div>
        <div className={css(styles.tableColumnNameDiv)}>{targetTableColumnName}</div>
      </div>
      <div className={css(styles.relationshipSvgDiv)}>
        <svg width="100%" height="100%">
          <polyline
            strokeLinejoin="round"
            points={stringifyPath(TOOLTIP_SVG_PATH)}
            fill="none"
            stroke={BLUE_DARKER}
            strokeWidth={2}
          />
          <polyline
            strokeLinejoin="round"
            points={stringifyPath(sourceDecoratorPath)}
            fill="none"
            stroke={BLUE_DARKER}
            strokeWidth={2}
          />
          <polyline
            strokeLinejoin="round"
            points={stringifyPath(targetDecoratorPath)}
            fill="none"
            stroke={BLUE_DARKER}
            strokeWidth={2}
          />
        </svg>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: 'fit-content',
    height: DWD_RELATIONSHIP_TOOLTIP_HEIGHT,
    backgroundColor: 'white',
    cursor: 'default',
    boxShadow: '0 0 5px #aaa',
    display: 'flex',
  },
  relationshipDataDiv: {
    paddingLeft: 6,
  },
  relationshipSvgDiv: {
    width: TOOLTIP_SVG_WIDTH,
    paddingLeft: 6,
  },
  tableColumnNameDiv: {
    height: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
});
